<script setup lang="ts">
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import { storeToRefs } from 'pinia'
import DynamicHeading from '@/components/DynamicHeading.vue'
import GoalCardLevelIndicators from '@/goals/GoalCardLevelIndicators.vue'
import BrowseGoalCardFooter from '@/goals/BrowseGoalCardFooter.vue'
import { BLURRED_TEXT_CLASSES, getPlaceholderText } from '@/util/placeholder_text'
import { hasGrowGoal } from '@/util/growGoalChecker'
import { keepingBetaOptIn } from '@/util/beta_opt_in'
import { useUserStore } from '@/authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { sendAlgoliaClickedBrowseGoalResultEvent } from '@/search/algolia_search'
import {
  browseGoalCardUrl,
  goalLevelLabel,
  hasStandardSet,
  PLACEHOLDER_STANDARD_REF,
  RESTRICTION_PREMIUM,
  shortStandardRef
} from '@/goals/browse_goal_card'
import type { BrowseGoalFilters, GoalCardRestriction, StandardsByLongRef } from '@/goals/browse_goal_card'
import type { GoalHit } from '@/goals/goal_types'

const PLACEHOLDER_TITLE_LENGTH = 40
const PLACEHOLDER_TEXT_LENGTH = 100

const props = withDefaults(defineProps<{
  goal: GoalHit
  restrictionType?: GoalCardRestriction
  headingLevel?: number
  position?: number
  standardSetLabel: string
  standardSetForRefDisplay: string
  standardsByLongRef?: StandardsByLongRef
  browseFilters?: BrowseGoalFilters
  algoliaQueryId?: string
  isAlignedGoalsTile?: boolean
}>(), {
  restrictionType: '',
  headingLevel: 3,
  position: -1,
  standardsByLongRef: () => ({}),
  browseFilters: () => ({}),
  algoliaQueryId: '',
  isAlignedGoalsTile: false
})

const { userId } = storeToRefs(useUserStore())

const isLocked = computed(() => props.restrictionType === RESTRICTION_PREMIUM)
const isAnchor = computed(() => props.goal.super_page)
const title = computed(() => (isLocked.value ? getPlaceholderText(PLACEHOLDER_TITLE_LENGTH) : props.goal.title))
const goalText = computed(() => (isLocked.value ? getPlaceholderText(PLACEHOLDER_TEXT_LENGTH) : DOMPurify.sanitize(props.goal.goal_text)))
const url = computed(() => keepingBetaOptIn(browseGoalCardUrl(props.goal, props.browseFilters)))
// The label is itself a standard set code (such as CA-ELD), so it doubles as the set to look for.
const footerStandardSetLabel = computed(() => {
  const labelledSetCode = props.standardSetLabel
  if (!hasStandardSet(props.goal, labelledSetCode)) {
    return ''
  }
  return props.standardSetLabel
})
const footerStandardRef = computed(() => {
  if (isLocked.value) {
    return PLACEHOLDER_STANDARD_REF
  }
  return shortStandardRef(props.goal, props.standardSetForRefDisplay, props.standardsByLongRef)
})

const analytics = getAnalyticsTracker().getChildContext({
  algolia_query_id: computed(() => props.algoliaQueryId || undefined),
  browse_goals_tile_goal_uuid: computed(() => props.goal.uuid),
  browse_goals_tile_goal_title: computed(() => props.goal.title),
  browse_goals_tile_is_anchor: isAnchor,
  browse_goals_tile_position: computed(() => props.position),
  browse_goals_tile_is_udl_anchor: false,
  browse_goals_tile_is_premium: isLocked,
  browse_goals_tile_is_invite_10: false,
  browse_goals_tile_is_unlocked: false
})

const clickedTileEventName = computed(() => {
  if (props.isAlignedGoalsTile) {
    return EVENT_NAMES.BROWSE_GOALS.CLICKED_ALIGNED_TILE
  }
  return EVENT_NAMES.BROWSE_GOALS.CLICKED_TILE
})

function recordClick() {
  const algoliaPosition = props.position + 1
  analytics.sendEvent(clickedTileEventName.value, {}, { cu: props.goal.uuid, ct: props.goal.title })
  if (props.algoliaQueryId) {
    sendAlgoliaClickedBrowseGoalResultEvent({
      userToken: userId.value,
      queryID: props.algoliaQueryId,
      objectIDs: [props.goal.objectID],
      positions: [algoliaPosition]
    })
  }
}

// An unlocked card records its click through the title link instead.
function recordLockedCardClick() {
  if (!isLocked.value) {
    return
  }
  recordClick()
}
</script>

<template>
  <div
    data-testid="browse-goal-card"
    class="group relative rounded-xxs bg-blue-100 shadow-lib-card"
    :class="[
      isLocked ? 'cursor-not-allowed' : 'hover:shadow-lib-card-hover'
    ]"
    @click="recordLockedCardClick"
  >
    <div class="h-[171px] overflow-hidden px-5 pt-5" :aria-hidden="isLocked || undefined">
      <DynamicHeading
        :heading-level="headingLevel"
        class="mb-[0.7em] overflow-hidden text-text-xl leading-5"
        :class="isLocked ? ['p-2', BLURRED_TEXT_CLASSES] : 'max-h-10 text-blue-500 group-hover:text-blue-600'"
      >
        <span v-if="isLocked">{{ title }}</span>
        <a
          v-else
          :href="url"
          class="after:absolute after:inset-0 after:rounded-xxs after:content-[''] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-blue-600"
          @click="recordClick"
        >{{ title }}</a>
      </DynamicHeading>
      <div class="text-text-md leading-5" :class="isLocked ? BLURRED_TEXT_CLASSES : 'text-gray-800'" data-testid="browse-goal-card-text" v-html="goalText" />
    </div>
    <div class="h-[54px] px-5">
      <GoalCardLevelIndicators
        :grades="goal.grades"
        :level="goal.level"
        :level-label="goalLevelLabel(goal)"
        :levels="goal.levels"
        :super-page="isAnchor"
        :blur-lock="isLocked"
      />
    </div>
    <div class="rounded-b-xxs bg-gray-200 px-5 py-3">
      <BrowseGoalCardFooter
        :standard-set-label="footerStandardSetLabel"
        :standard-ref="footerStandardRef"
        :show-progress="goal.has_progress_monitoring"
        :resource-languages="goal.resource_languages ?? []"
        :show-grow="hasGrowGoal(goal.uuid)"
        :blur-lock="isLocked"
      />
    </div>
    <span
      v-if="isLocked"
      class="absolute bottom-2.5 right-3 rounded-xxs bg-red-300 px-1.5 py-0.5 text-[13px] font-bold text-white"
      data-testid="premium-badge"
    >
      <span class="material-icons-round align-middle text-[1em]" aria-hidden="true">lock</span> PREMIUM
    </span>
  </div>
</template>
